import React, { Fragment, useEffect, useContext } from 'react';
import proptypes from 'prop-types';
const { arrayOf, bool, string } = proptypes;
import {
  Alert,
  Link,
  Skeleton,
  SkeletonBlock,
  SkeletonLine,
  Typography
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { usePartsAndServices } from '../PartsAndServicesProvider';
import { Pod } from './Pod';
import { ProtectionPlanRadioBtns } from './ProtectionPlanRadioBtns';
import {
  INFO_ALERT_PROTECTION,
  PROTECTION_DETAILS_URL,
  PROTECTION_NOTE,
  PROTECTION_TITLE,
  PROTECTION_EMPTY,
  ADD_ON_CATEGORIES,
  INFO_ALERT_SELECTIONS,
  PARTS_AND_SERVICES_DOWN_ALERT_MSG
} from '../../constants';
import { protectionPointsData, b2bProtectionPointsData } from '../../staticData';
import { track } from '../../parts-and-services-analytics';
import { capitalize, filterByCategoryAndType } from '../../utils';

export const Protection = ({
  exclusionListProtection,
  hideTitle,
  showAlert,
  showZipCodeAlert
}) => {
  const {
    disableHeader,
    enableInstallationFirst,
    productsMap,
    showLoadingSkeleton,
    isPSPPServiceDown,
  } = usePartsAndServices();

  const { customer } = useContext(ExperienceContext);

  const formattedTitle = capitalize(
    enableInstallationFirst ? `Next, ${PROTECTION_TITLE}` : PROTECTION_TITLE
  );

  const isB2BCustomer = customer?.type === 'b2b';
  const protectionPoints = isB2BCustomer ? b2bProtectionPointsData : protectionPointsData;

  useEffect(() => {
    track('displayed-hdpp');

    // TODO remove once iframe is gone
    if (!disableHeader) {
      window.scrollTo({ top: 0 });
    }
  }, []);

  return (
    <div data-testid="protection-page" className="sui-flex sui-flex-col">
      {showAlert && (
        <Alert status="info">
          <Typography>{INFO_ALERT_PROTECTION}</Typography>
        </Alert>
      )}
      {showZipCodeAlert && (
        <div className="sui-pt-2">
          <Alert status="info">
            <Typography>{INFO_ALERT_SELECTIONS}</Typography>
          </Alert>
        </div>
      )}
      {!hideTitle && (
        <div className="sui-mb-4">
          <Typography variant="h3">{formattedTitle}</Typography>
        </div>
      )}
      <div className="sui-flex sui-mb-2">
        <img
          alt="HDPP by Allstate"
          className="sui-object-contain sui-self-start sui-mt-1"
          src="https://assets.thdstatic.com/images/v1/hdpp-allstate.svg"
          title="HDPP by Allstate"
          width="55px"
          height="77.49px"
        />
        <div>
          <ul className="sui-ml-6 sui-pl-2 sui-list-disc">
            {protectionPoints.map(({ description }) => {
              return (
                <li key={description}>
                  <Typography variant="body-base">
                    {description}
                  </Typography>
                </li>
              );
            })}
          </ul>
          <div className="sui-ml-2 sui-mt-2">
            <div className="sui-mb-2">
              <Link href={PROTECTION_DETAILS_URL} target="_blank" underline="always">
                Plan Details
              </Link>
            </div>
            <Typography variant="body-xs">
              {PROTECTION_NOTE}
            </Typography>
          </div>
        </div>
      </div>
      <hr className="sui-my-4" />
      {isPSPPServiceDown && (
        <Alert status="error">
          <Typography>{PARTS_AND_SERVICES_DOWN_ALERT_MSG}</Typography>
        </Alert>
      )}
      <div className={isPSPPServiceDown ? '' : 'sui-pb-2'}>
        {showLoadingSkeleton && (
          <div className="sui-mt-5 sui-mb-5">
            <Skeleton disablePadding grow>
              <SkeletonBlock />
              <SkeletonLine variant="single" grow />
            </Skeleton>
          </div>
        )}
        {!showLoadingSkeleton && !isPSPPServiceDown && (
          <div>
            {[...productsMap.entries()]
              .filter(([lineItemId]) => !exclusionListProtection.includes(lineItemId))
              .map(([lineItemId, product], productIndex) => {
                const { addOns = [] } = product || {};
                const protectionPlans = filterByCategoryAndType({
                  addOns,
                  lineItemId,
                  addOnCategories: ADD_ON_CATEGORIES.PROTECTION
                }).sort((first, second) => second.termLength - first.termLength);
                return (
                  <Fragment key={lineItemId}>
                    <div>
                      <Pod lineItemId={lineItemId} product={product} />
                      <div className="sui-mt-2">
                        {protectionPlans.length !== 0 ? (
                          <ProtectionPlanRadioBtns
                            lineItemId={lineItemId}
                            protectionPlans={protectionPlans}
                          />
                        ) : (<Alert status="warning">{PROTECTION_EMPTY}</Alert>)}
                      </div>
                    </div>
                    {productsMap.size > 1 && productIndex < productsMap.size - 1 && (<hr className="sui-my-4" />)}
                  </Fragment>
                );
              }
              )}
          </div>
        )}
      </div>
    </div>
  );
};

Protection.propTypes = {
  exclusionListProtection: arrayOf(string).isRequired,
  hideTitle: bool,
  showAlert: bool,
  showZipCodeAlert: bool
};

Protection.defaultProps = {
  hideTitle: false,
  showAlert: false,
  showZipCodeAlert: false
};

Protection.displayName = 'Protection';
